import * as React from 'react'

import { CounselingCard } from '~/molecules/card/CounselingCard'

import MainImage from './main.png'
import MainImageSP from './main_sp.png'
import Case1Image from './case1.svg?reactCompornt'
import Case2Image from './case2.svg?reactCompornt'
import Case3Image from './case3.svg?reactCompornt'
import UserImage from './user.svg?reactCompornt'
import CheckIcon from './check.svg?reactCompornt'
import SupporterImage from './supporter.svg?reactCompornt'
import ExampleImage1 from './example_image1.png'
import ExampleImage2 from './example_image2.png'
import ClockIcon from './clock.svg?reactCompornt'
import WatchIcon from './watch.svg?reactCompornt'
import PhoneIcon from './phone.svg?reactCompornt'
import ArrowIcon from './arrow.svg?reactCompornt'
import LeftArrowIcon from './leftarrow.svg?reactCompornt'
import MoneyIcon from './money.svg?reactCompornt'
import TimerIcon from './timer.svg?reactCompornt'
import CoinIcon from './coin.svg?reactCompornt'
import MoneyIconSP from './money_sp.svg?reactCompornt'
import TimerIconSP from './timer_sp.svg?reactCompornt'
import CoinIconSP from './coin_sp.svg?reactCompornt'
import CrossIcon from './cross.svg?reactCompornt'
import EqualIcon from './equal.svg?reactCompornt'

import * as Style from './style.css'
import classNames from 'classnames'

export interface IProps {
  counselors: Omit<ICounselorDetail, 'id' | 'userId'>[]
}

export const CounselingLP = ({ counselors, ...props }: IProps): JSX.Element => {
  const sections = [
    { id: 'about', label: '相談内容' },
    { id: 'supporter', label: 'サポーター' },
    { id: 'bonus', label: '特典' },
    { id: 'flow', label: '流れ' },
    { id: 'pricing', label: '料金' },
  ]

  const [active, setActive] = React.useState(sections[0].id)

  const handleClick = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault()
    const el = document.getElementById(id)
    if (!el) return

    const navHeight = 53
    const rect = el.getBoundingClientRect()
    const offsetTop = window.pageYOffset + rect.top - navHeight + 9

    window.scrollTo({ top: offsetTop, behavior: 'smooth' })
  }

  React.useEffect(() => {
    const headerOffset = 53
    const opts: IntersectionObserverInit = {
      root: null,
      rootMargin: `-${headerOffset + 8}px 0px -90% 0px`,
      threshold: 0,
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) setActive(entry.target.id)
      })
    }, opts)

    sections.forEach(({ id }) => {
      const el = document.getElementById(id)
      if (el) io.observe(el)
    })
    return () => io.disconnect()
  }, [])

  const footerRef = React.useRef<HTMLDivElement>(null)
  const [hideFooter, setHideFooter] = React.useState(false)
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        setHideFooter(entry.isIntersecting)
      },
      { threshold: 0.3 },
    )

    if (footerRef.current) {
      observer.observe(footerRef.current)
    }

    return () => {
      if (footerRef.current) {
        observer.unobserve(footerRef.current)
      }
    }
  }, [])

  return (
    <div className={Style.Layout}>
      <nav className={Style.NaviMenu}>
        <ul className={Style.MenuList}>
          {sections.map(({ id, label }) => (
            <li key={id} className={Style.MenuItem}>
              <a
                href={id}
                onClick={handleClick(id)}
                className={classNames({
                  [Style.MenuLink]: true,
                  [Style.MenuLinkActive]: active === id,
                })}
              >
                {label}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      {!hideFooter && (
        <div className={Style.Footer}>
          <div className={Style.FooterOffer}>
            <div className={Style.FooterOfferBadge}>今だけ</div>
            <span className={Style.FooterOfferPrefix}>初回相談</span>
            <p className={Style.MainVisualOfferMarker}>
              <span>5</span>
              <span className={Style.MainVisualOfferMarkerRight}>分無料</span>
            </p>
          </div>
          <div className={Style.FooterLinkArea}>
            <span className={Style.FooterLinkInfo}>
              ※サポーターの閲覧にはログインが必要です。
            </span>
            <a href="/counselors" className={Style.FooterLink}>
              今すぐ相談できるサポーター
            </a>
          </div>
        </div>
      )}

      <section id="top" className={Style.MainVisual}>
        <div className={Style.MainVisualContents}>
          <div className={Style.MainVisualTextArea}>
            <div className={Style.MainVisualTop}>
              <div className={Style.MainVisualTopTime}>
                <ClockIcon
                  className={Style.MainVisualClockIcon}
                  viewBox="0 0 20 20"
                />
                <div className={Style.MainVisualTopTimeText}>
                  <div className={Style.MainVisualTopTimeTextTime}>
                    <span>24</span>
                    <span className={Style.MainVisualTopTimeTextUnit}>
                      時間
                    </span>
                  </div>
                  <div className={Style.MainVisualTopTimeTextTime}>
                    <span>365</span>
                    <span className={Style.MainVisualTopTimeTextUnit}>日</span>
                  </div>
                </div>
              </div>

              <div className={Style.MainVisualTitle}>
                <h1 className={Style.MainVisualTitleMain}>つながる相談室</h1>
                <span className={Style.MainVisualTitleSub}>
                  あなたの悩みに、今すぐ寄りそう
                  <br />
                  オンライン通話相談
                </span>
              </div>
            </div>
            <div className={Style.MainVisualOffer}>
              <div className={Style.MainVisualOfferBadge}>今だけ</div>
              <span className={Style.MainVisualOfferPrefix}>初回相談</span>
              <p className={Style.MainVisualOfferMarker}>
                <span>5</span>
                <span className={Style.MainVisualOfferMarkerRight}>分無料</span>
              </p>
            </div>
          </div>
          <div
            className={Style.MainVisualPhoto}
            style={{
              background: `linear-gradient(to right, white 370px, rgba(255, 255, 255, 0.9) 390px, rgba(255, 255, 255, 0.7) 420px, rgba(255, 255, 255, 0.4) 480px, rgba(255, 255, 255, 0) 590px), url(${MainImage}) right center no-repeat`,
            }}
          ></div>
        </div>

        <div className={Style.MainVisualContentsSP}>
          <div className={Style.MainVisualTextArea}>
            <div className={Style.MainVisualTop}>
              <div className={Style.MainVisualTopTime}>
                <ClockIcon
                  className={Style.MainVisualClockIcon}
                  viewBox="0 0 20 20"
                />
                <div className={Style.MainVisualTopTimeText}>
                  <div className={Style.MainVisualTopTimeTextTime}>
                    <span>24</span>
                    <span className={Style.MainVisualTopTimeTextUnit}>
                      時間
                    </span>
                  </div>
                  <div className={Style.MainVisualTopTimeTextTime}>
                    <span>365</span>
                    <span className={Style.MainVisualTopTimeTextUnit}>日</span>
                  </div>
                </div>
              </div>

              <div className={Style.MainVisualTitle}>
                <h1 className={Style.MainVisualTitleMain}>つながる相談室</h1>
                <span className={Style.MainVisualTitleSub}>
                  あなたの悩みに、今すぐ寄りそう
                  <br />
                  オンライン通話相談
                </span>
              </div>
            </div>
            <div className={Style.MainVisualOffer}>
              <div className={Style.MainVisualOfferBadge}>今だけ</div>
              <span className={Style.MainVisualOfferPrefix}>初回相談</span>
              <p className={Style.MainVisualOfferMarker}>
                <span>5</span>
                <span className={Style.MainVisualOfferMarkerRight}>分無料</span>
              </p>
            </div>
          </div>
          <div
            className={Style.MainVisualPhotoSP}
            style={{
              background: `linear-gradient(to right, white 0px, rgba(255, 255, 255, 0.9) 90px, rgba(255, 255, 255, 0.7) 125px, rgba(255, 255, 255, 0.4) 150px, rgba(255, 255, 255, 0) 185px), url(${MainImageSP}) right center no-repeat`,
            }}
          ></div>
        </div>
      </section>

      <section id="about" className={Style.About}>
        <div className={Style.AboutTop}>
          <h1 className={Style.SectionTitle}>不安や悩み</h1>
          <p className={Style.AboutSectionSubTitle}>
            ひとりで抱えていませんか？
          </p>
        </div>
        <div className={Style.CounselingTypes}>
          <div className={Style.CounselingTypeIcons}>
            <span className={Style.CounselingTypeIcon}>子育て</span>
            <span className={Style.CounselingTypeIcon}>人間関係</span>
            <span className={Style.CounselingTypeIcon}>気持ちの整理</span>
          </div>
          <p className={Style.CounselingTypeText}>など</p>
        </div>
        <div className={Style.CounselingExamples}>
          <div className={Style.SectionSubTitle}>
            <h2 className={Style.SectionSubTitleText}>相談例</h2>
          </div>
          <div className={Style.Cases}>
            <div className={Style.Case}>
              <div className={Style.Request}>
                <div className={Style.RequestBubble}>
                  <span className={Style.RequestText}>
                    3歳差育児、上の子のかんしゃくが強くてつらいです
                  </span>
                  <div className={Style.RequestImage}>
                    <Case1Image />
                  </div>
                </div>
                <div className={Style.UserAvatar}>
                  <UserImage />
                </div>
              </div>

              <div className={Style.Offer}>
                <div className={Style.SupporterAvatar}>
                  <SupporterImage />
                </div>
                <div className={Style.OfferBubble}>
                  <span className={Style.OfferText}>
                    声かけや対応のコツを、お子様やご家庭の状況に合わせてご提案します。
                  </span>
                </div>
              </div>

              <div className={Style.CaseDetails}>
                <div className={Style.CaseDetail}>
                  <div className={Style.CaseIcon}>
                    <WatchIcon />
                  </div>
                  <p className={Style.CaseText}>
                    おすすめ時間帯：きょうだいが眠った夜の時間帯
                  </p>
                </div>
                <div className={Style.CaseDetail}>
                  <div className={Style.CaseIcon}>
                    <PhoneIcon />
                  </div>
                  <p className={Style.CaseText}>おすすめ通話時間：20分程度</p>
                </div>
              </div>
            </div>

            <div className={Style.Case}>
              <div className={Style.Request}>
                <div className={Style.RequestBubble}>
                  <span className={Style.RequestText}>
                    赤ちゃんが離乳食を食べない…
                    <br className={Style.SpOnly} />
                    今すぐできる工夫を知りたい
                  </span>
                  <div className={Style.RequestImage}>
                    <Case2Image />
                  </div>
                </div>
                <div className={Style.UserAvatar}>
                  <UserImage />
                </div>
              </div>

              <div className={Style.Offer}>
                <div className={Style.SupporterAvatar}>
                  <SupporterImage />
                </div>
                <div className={Style.OfferBubble}>
                  <span className={Style.OfferText}>
                    赤ちゃんの食事中でも大丈夫。受けたアドバイスをその場ですぐに試せます。
                  </span>
                </div>
              </div>

              <div className={Style.CaseDetails}>
                <div className={Style.CaseDetail}>
                  <div className={Style.CaseIcon}>
                    <WatchIcon />
                  </div>
                  <p className={Style.CaseText}>
                    おすすめ時間帯：朝・昼・夕など離乳食のタイミング前後
                  </p>
                </div>
                <div className={Style.CaseDetail}>
                  <div className={Style.CaseIcon}>
                    <PhoneIcon />
                  </div>
                  <p className={Style.CaseText}>おすすめ通話時間：30分程度</p>
                </div>
              </div>
            </div>

            <div className={Style.Case}>
              <div className={Style.Request}>
                <div className={Style.RequestBubble}>
                  <span className={Style.RequestText}>
                    授乳しても泣き止まず、
                    <br className={Style.SpOnly} />
                    泣き声に向き合うのがつらいです
                  </span>
                  <div className={Style.RequestImage}>
                    <Case3Image />
                  </div>
                </div>
                <div className={Style.UserAvatar}>
                  <UserImage />
                </div>
              </div>

              <div className={Style.Offer}>
                <div className={Style.SupporterAvatar}>
                  <SupporterImage />
                </div>
                <div className={Style.OfferBubble}>
                  <span className={Style.OfferText}>
                    泣き声があっても気にせずにご相談ください。話しながら一緒に気持ちを整理しましょう。
                  </span>
                </div>
              </div>

              <div className={Style.CaseDetails}>
                <div className={Style.CaseDetail}>
                  <div className={Style.CaseIcon}>
                    <WatchIcon />
                  </div>
                  <p className={Style.CaseText}>
                    おすすめ時間帯：赤ちゃんが泣いている時でもOK
                  </p>
                </div>
                <div className={Style.CaseDetail}>
                  <div className={Style.CaseIcon}>
                    <PhoneIcon />
                  </div>
                  <p className={Style.CaseText}>おすすめ通話時間：20分程度</p>
                </div>
              </div>
            </div>
          </div>
          <div className={Style.AdditionalInfo}>
            <div className={Style.AdditionalInfoTop}>
              その他のシーンでももちろんご相談ください！
              <br className={Style.SpOnly} />
              ご相談可能なお悩み例の詳細は
              <a
                href="https://kidsline.me/help/center_detail/advice_service_user_example"
                className={Style.CaseLink}
                target="_blank"
              >
                こちら
              </a>
            </div>
            <span className={Style.HelpLinkBox}>
              ※医療に関する判断など、一部お受けできないご相談内容もございます。詳細は
              <a
                href="https://kidsline.me/help/center_detail/advice_service_user_prohibited"
                className={Style.HelpLink}
                target="_blank"
              >
                こちら
              </a>
            </span>
          </div>
        </div>
      </section>

      <section id="supporter" className={Style.Supporter}>
        <div className={Style.SupporterTop}>
          <h1 className={Style.SupporterSectionTitle}>こんなサポーター</h1>
          <p className={Style.SupporterSectionSubTitle}>が相談をお聞きします</p>
        </div>

        <div className={Style.SupporterInfos}>
          <div className={Style.SupporterCopys}>
            <div className={Style.SupporterCopy}>
              <CheckIcon
                viewBox="0 0 16 16"
                className={Style.SupporterCopyIcon}
              />
              <span className={Style.SupporterCopyText}>
                資格や経験豊富なサポーター
              </span>
            </div>
            <div className={Style.SupporterCopy}>
              <CheckIcon
                viewBox="0 0 16 16"
                className={Style.SupporterCopyIcon}
              />
              <span className={Style.SupporterCopyText}>
                様々なお悩みに対応可能
              </span>
            </div>
          </div>

          <div className={Style.RecomendSupporterTitle}>
            <div className={Style.SectionSubTitle}>
              <h2 className={Style.SectionSubTitleText}>
                今すぐ相談可能なサポーター
              </h2>
            </div>
            <span className={Style.RecomendSupporterNote}>
              ※時間帯によってはサポーターがいない場合がございます。
              <br />
              ※サポーターの閲覧にはログインが必要です。
            </span>
          </div>

          <ul className={Style.RecomendSupporters}>
            {counselors.map((counselor, index) => {
              return (
                <li key={index} className={Style.RecomendSupporterCard}>
                  <CounselingCard
                    catchcopy={counselor.catchcopy}
                    specialtyConsultations={counselor.specialtyConsultations}
                    pricePerMinute={counselor.pricePerMinute}
                    reviewScore={counselor.reviewScore}
                    reviewCount={counselor.reviewCount}
                    imageUrl={counselor.imageUrl}
                    showUrl={counselor.showUrl}
                    editUrl={counselor.editUrl}
                    progressCounseling={counselor.progressCounseling}
                    nowStatus={counselor.nowStatus}
                    certs={counselor.certs}
                    certsIncludeTraining={counselor.certsIncludeTraining}
                  />
                </li>
              )
            })}
          </ul>
        </div>

        <a href="/counselors" className={Style.RecomendSupportersLink}>
          他のサポーターを見る
        </a>
      </section>

      <section id="bonus" className={Style.Bonus}>
        <div className={Style.BonusMain}>
          <div className={Style.BonusBadge}>今だけ</div>
          <div className={Style.BonusTop}>
            <p className={Style.BonusHead}>
              <span className={Style.BonusHeadMarker}>初回5分</span>
            </p>
            <p className={Style.BonusTitle}>
              <span className={Style.BonusTitleMarker}>
                <span className={Style.BonusTitlePrefix}>初回5分</span>
                <span className={Style.BonusTitleLeft}>無料相談</span>
                <span className={Style.BonusTitleRight}>プレゼント</span>
              </span>
            </p>
          </div>
          <div className={Style.BonusDescription}>
            「つながる相談室」スタート記念として、初回5分の無料相談をプレゼントいたします！この機会にぜひお試しください。
          </div>
          <div className={Style.Devider}></div>
          <div className={Style.BonusInfo}>
            ・詳細：つながる相談室の初回ご利用時、最初の5分が無料で相談いただけます。
          </div>
          <div className={Style.BonusWorning}>
            ※5分無料が適用となる場合も、最低通話時間（10分）に満たない分の料金は発生します。
          </div>
        </div>
      </section>

      <section id="flow" className={Style.Flow}>
        <h1 className={Style.SectionTitle}>ご利用の流れ</h1>
        <div className={Style.Steps}>
          <div className={Style.StepCard}>
            <div className={Style.StepTitle}>
              <div className={Style.StepNumber}>1</div>
              <div className={Style.StepName}>
                <span>サポーターを選ぶ</span>
              </div>
            </div>
            <div className={Style.StepDescription}>
              相談したいサポーターを選びます。
              <br />
              「得意なことから選ぶ」「過去に相談してよかった人を探す」などの方法で探すことができます。
            </div>
          </div>

          <ArrowIcon className={Style.ArrowIcon} />
          <div className={Style.ArrowIconBox}>
            <LeftArrowIcon />
          </div>

          <div className={Style.StepCard}>
            <div className={Style.StepTitle}>
              <div className={Style.StepNumber}>2</div>
              <div className={Style.StepName}>
                <span>相談したい内容を入力</span>
                <span className={Style.StepNameSub}>(任意)</span>
              </div>
            </div>
            <div className={Style.StepDescription}>
              相談内容や希望の相談スタイルを選んで申し込みます。
              <br />
              ※事前に入力いただくと、よりスムーズに、時間を有効に使ってお話しいただけます。
            </div>
          </div>

          <ArrowIcon className={Style.ArrowIcon} />
          <div className={Style.ArrowIconBox}>
            <LeftArrowIcon />
          </div>

          <div className={Style.StepCard}>
            <div className={Style.StepTitle}>
              <div className={Style.StepNumber}>3</div>
              <div className={Style.StepName}>
                <span>相談する</span>
              </div>
            </div>
            <div className={Style.StepDescription}>
              相談ルームに入室すると通話が始まります。
              <br />
              終了後にご登録のクレジットカードで自動決済がされます。
            </div>
          </div>
        </div>
        <div className={Style.StepFooter}>
          ※操作画面付きの詳細は
          <a
            href="/information/soudan25"
            className={Style.StepFooterLink}
            target="_blank"
          >
            こちら
          </a>
          <br />
          ※より良い相談のための準備については
          <a
            href="https://kidsline.me/help/center_detail/advice_service_user_ready"
            className={Style.StepFooterLink}
            target="_blank"
          >
            こちら
          </a>
        </div>
      </section>

      <section id="pricing" className={Style.Pricing}>
        <h1 className={Style.SectionTitle}>料金について</h1>

        <div className={Style.PricingContent}>
          <div className={Style.PricingIllustration}>
            <div className={Style.PricingIllustrationTop}>
              <div className={Style.PricingItem}>
                <span className={Style.PricingItemLabel}>1分あたり単価</span>
                <MoneyIcon
                  className={Style.PricingItemIcon}
                  viewBox="0 0 36 36"
                />
              </div>
              <div className={Style.PricingCalcIcon}>
                <CrossIcon />
              </div>
              <div className={Style.PricingItem}>
                <span className={Style.PricingItemLabel}>通話時間</span>
                <TimerIcon
                  className={Style.PricingItemIcon}
                  viewBox="0 0 36 36"
                />
              </div>
              <div className={Style.PricingCalcIcon}>
                <EqualIcon />
              </div>
              <div className={Style.PricingItem}>
                <span className={Style.PricingItemLabel}>お支払い料金</span>
                <CoinIcon
                  className={Style.PricingItemIcon}
                  viewBox="0 0 36 36"
                />
              </div>
            </div>

            <div className={Style.PricingIllustrationText}>
              <div className={Style.PricingIllustrationTextTop}>
                <span>1分あたり単価×通話時間がお支払い料金となります。</span>
                <span>
                  ※1分あたりの単価は最低150円～です。
                  <br />
                  ※最低通話時間は10分です。
                </span>
              </div>
              <span>
                また本サービスでは、通話1分あたりの単価をサポーターがそれぞれ設定しています。
              </span>
              <span>
                【注意】
                <br />
                通信トラブルや満足度にかかわらず、通話時間に応じたお支払い料金となりますので、通信環境が整った状態で相談を行なってください。
                <br />
                通話中の経過時間はご自身でご確認をお願いします。
              </span>
            </div>
          </div>

          <div className={Style.PricingIllustrationSp}>
            <div className={Style.PricingIllustrationTop}>
              <div className={Style.PricingItem}>
                <span className={Style.PricingItemLabel}>1分あたり単価</span>
                <MoneyIconSP
                  className={Style.PricingItemIcon}
                  viewBox="0 0 36 36"
                />
              </div>
              <div className={Style.PricingCalcIcon}>
                <CrossIcon />
              </div>
              <div className={Style.PricingItem}>
                <span className={Style.PricingItemLabel}>通話時間</span>
                <TimerIconSP
                  className={Style.PricingItemIcon}
                  viewBox="0 0 36 36"
                />
              </div>
              <div className={Style.PricingCalcIcon}>
                <EqualIcon />
              </div>
              <div className={Style.PricingItem}>
                <span className={Style.PricingItemLabel}>お支払い料金</span>
                <CoinIconSP
                  className={Style.PricingItemIcon}
                  viewBox="0 0 36 36"
                />
              </div>
            </div>

            <div className={Style.PricingIllustrationText}>
              <div className={Style.PricingIllustrationTextTop}>
                <span>1分あたり単価×通話時間がお支払い料金となります。</span>
                <span>
                  ※1分あたりの単価は最低150円～です。
                  <br />
                  ※最低通話時間は10分です。
                </span>
              </div>
              <span>
                また本サービスでは、通話1分あたりの単価をサポーターがそれぞれ設定しています。
              </span>
            </div>
          </div>

          <div className={Style.PricingExamples}>
            <div className={Style.PricingExample}>
              <div className={Style.PricingExampleContent}>
                <span className={Style.PricingExampleTitle}>お支払い例①</span>
                <div className={Style.PricingExampleRow}>
                  <img
                    src={ExampleImage1}
                    className={Style.PricingExampleImage}
                  />
                  <div className={Style.PricingExampleText}>
                    3人の子育て経験のあるサポーターに、上の子との関わり方について30分相談した場合
                  </div>
                </div>
              </div>
              <div className={Style.Devider}></div>
              <div className={Style.PricingExampleCalc}>
                <div className={Style.PricingItem}>
                  <span className={Style.PricingItemCalcLabel}>
                    1分あたり単価
                  </span>
                  <div className={Style.PricingItemCalcContent}>
                    <MoneyIcon
                      className={Style.CalcContent}
                      width={24}
                      height={24}
                      viewBox="0 0 36 36"
                    />
                    <MoneyIconSP
                      className={Style.CalcContentSP}
                      width={24}
                      height={24}
                      viewBox="0 0 36 36"
                    />
                    <span>150円</span>
                  </div>
                </div>
                <div className={Style.PricingItemCalcIcon}>
                  <CrossIcon />
                </div>
                <div className={Style.PricingItem}>
                  <span className={Style.PricingItemCalcLabel}>通話時間</span>
                  <div className={Style.PricingItemCalcContent}>
                    <TimerIcon
                      className={Style.CalcContent}
                      width={24}
                      height={24}
                      viewBox="0 0 36 36"
                    />
                    <TimerIconSP
                      className={Style.CalcContentSP}
                      width={24}
                      height={24}
                      viewBox="0 0 36 36"
                    />
                    <span>30分</span>
                  </div>
                </div>
                <div className={Style.PricingItemCalcIcon}>
                  <EqualIcon />
                </div>
                <div className={Style.PricingItem}>
                  <span className={Style.PricingItemCalcLabel}>
                    お支払い料金
                  </span>
                  <div className={Style.PricingItemCalcContent}>
                    <CoinIcon
                      className={Style.CalcContent}
                      width={24}
                      height={24}
                      viewBox="0 0 36 36"
                    />
                    <CoinIconSP
                      className={Style.CalcContentSP}
                      width={24}
                      height={24}
                      viewBox="0 0 36 36"
                    />
                    <span>4,500円</span>
                  </div>
                </div>
              </div>
            </div>

            <div className={Style.PricingExample}>
              <div className={Style.PricingExampleContent}>
                <span className={Style.PricingExampleTitle}>お支払い例②</span>
                <div className={Style.PricingExampleRow}>
                  <img
                    src={ExampleImage2}
                    className={Style.PricingExampleImage}
                  />
                  <div className={Style.PricingExampleText}>
                    保育士経験と栄養士資格のあるサポーターに、離乳食について20分相談した場合
                  </div>
                </div>
              </div>
              <div className={Style.Devider}></div>
              <div className={Style.PricingExampleCalc}>
                <div className={Style.PricingItem}>
                  <span className={Style.PricingItemCalcLabel}>
                    1分あたり単価
                  </span>
                  <div className={Style.PricingItemCalcContent}>
                    <MoneyIcon
                      className={Style.CalcContent}
                      width={24}
                      height={24}
                      viewBox="0 0 36 36"
                    />
                    <MoneyIconSP
                      className={Style.CalcContentSP}
                      width={24}
                      height={24}
                      viewBox="0 0 36 36"
                    />
                    <span>170円</span>
                  </div>
                </div>
                <div className={Style.PricingItemCalcIcon}>
                  <CrossIcon />
                </div>
                <div className={Style.PricingItem}>
                  <span className={Style.PricingItemCalcLabel}>通話時間</span>
                  <div className={Style.PricingItemCalcContent}>
                    <TimerIcon
                      className={Style.CalcContent}
                      width={24}
                      height={24}
                      viewBox="0 0 36 36"
                    />
                    <TimerIconSP
                      className={Style.CalcContentSP}
                      width={24}
                      height={24}
                      viewBox="0 0 36 36"
                    />
                    <span>20分</span>
                  </div>
                </div>
                <div className={Style.PricingItemCalcIcon}>
                  <EqualIcon />
                </div>
                <div className={Style.PricingItem}>
                  <span className={Style.PricingItemCalcLabel}>
                    お支払い料金
                  </span>
                  <div className={Style.PricingItemCalcContent}>
                    <CoinIcon
                      className={Style.CalcContent}
                      width={24}
                      height={24}
                      viewBox="0 0 36 36"
                    />
                    <CoinIconSP
                      className={Style.CalcContentSP}
                      width={24}
                      height={24}
                      viewBox="0 0 36 36"
                    />
                    <span>3,400円</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={Style.PricingWarning}>
          【注意】
          <br />
          通信トラブルや満足度にかかわらず、通話時間に応じたお支払い料金となりますので、通信環境が整った状態で相談を行なってください。
          <br />
          通話中の経過時間はご自身でご確認をお願いします。
        </div>

        <div className={Style.PricingFooter} ref={footerRef}>
          <div className={Style.PricingFooterLinkArea}>
            <span className={Style.FooterLinkInfo}>
              ※サポーターの閲覧にはログインが必要です。
            </span>
            <a className={Style.SupporterIndexLink} href="/counselors">
              今すぐ相談できるサポーター
            </a>
          </div>
          <p className={Style.HelpLinkText}>
            よくある質問は
            <a
              href="/help/advice_service/center_list"
              className={Style.HelpLink}
              target="_blank"
            >
              こちら
            </a>
          </p>
        </div>
      </section>
    </div>
  )
}
